import { Container, Divider } from "@material-ui/core";
import { Box, Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteWithSearch,
  BalanceCard,
  CurrencyDisplay,
  CustomTable,
  ModalError,
  ModalTable,
  Page,
  SearchTextInput,
  SelectInput,
  StatusChip,
  YearPicker,
} from "components";
import { ButtonGroupTop, PrimaryTextButton } from "components/atoms/Button";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import {
  formatDate,
  formatPeriodFromString,
  getErrors,
  wordCapitalize,
} from "utils";
import { optionsStatusAdvanceComposer } from "./Components/Constants";

const AdvanceComposer = () => {
  const classes = globalStyles();
  const location = useLocation();
  const history = useHistory();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search") || "";
  const paramsPage = urlParams?.get("page") || 1;
  const paramsSize = urlParams?.get("size") || 10;
  const paramsStatus = urlParams?.get("status") || "";
  const paramsYear = urlParams?.get("year");
  const paramsComposer = urlParams?.get("composer_id");

  const [optionClient, setOptionClient] = useState([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
    status: paramsStatus || "",
    sort: "",
    year: Number(paramsYear) || moment().year(),
    composer_id: Number(paramsComposer) || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [dataSummary, setDataSummary] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filterParams, setFilterParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  const [loadingModal, setLoadingModal] = useState(false);
  const [dataModal, setDataModal] = useState([]);
  const [modalParams, setModalParams] = useState({
    page: 1,
    size: 10,
    search: "",
    status: "",
  });
  const [modalTotalPage, setModalTotalPage] = useState(1);
  const [modalSettlement, setModalSettlement] = useState(false);
  const filterSameKey = optionClient.filter(
    (item, index, self) =>
      index === self.findIndex(t => t.composer_id === item.composer_id)
  );
  const filterComposer = filterSameKey?.filter(item =>
    item?.composer_name
      ?.toLowerCase()
      ?.includes(filterParams?.search?.toLowerCase())
  );

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };
  const handleChangeModalParams = (value, key) => {
    setModalParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeFilterParams = (value, key) => {
    setFilterParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleViewSettlement = item => {
    setModalSettlement(true);
    getDetailSettlement(item?.id);
  };

  const getClientList = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(
        `${hardBaseUrl}/advance-composer/v2/list-composer`,
        {
          headers,
          params: {
            ...filterParams,
            year: queryParams?.year,
          },
        }
      );
      const { data, meta } = res?.data;
      const newOptions = data?.map(item => ({
        ...item,
        id: item?.composer_id,
      }));

      setOptionClient(prevOptions => {
        const oldData = Array.isArray(prevOptions) ? prevOptions : [];
        return [...oldData, ...newOptions];
      });

      setHasMore(meta?.total > newOptions.length);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };
  const debounceFilterClient = useCallback(
    debounce(() => {
      getClientList();
    }, 500),
    [filterParams, queryParams?.year]
  );
  const getDataSummary = async () => {
    const { year, composer_id } = queryParams;
    try {
      const res = await axios.get(
        `${hardBaseUrl}/advance-composer/v2/summary`,
        {
          headers,
          params: {
            year,
            composer_id,
          },
        }
      );
      const { data } = res.data;
      const categories = ["total", "closed", "open"];
      const modifiedData = categories.map(category => {
        return {
          card_name: `${wordCapitalize(
            category === "closed"
              ? "Settled"
              : category === "open"
              ? "Unsettled"
              : category
          )}`,
          total_amount: data?.[`${category}_amount`] || 0,
          total_document: data?.[`${category}_document`] || 0,
          type: category,
        };
      });
      setDataSummary(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataTable = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/advance-composer/v2/list`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const debounceDataTable = useCallback(
    debounce(async () => {
      setLoadingPage(true);
      await getDataTable();
      setLoadingPage(false);
    }, 500),
    [queryParams]
  );

  const getDataModal = async () => {
    try {
      setLoadingModal(true);
      const { composer_id, year } = queryParams;
      const res = await axios.get(`${hardBaseUrl}/advance-composer/v2/list`, {
        headers,
        params: {
          ...modalParams,
          composer_id,
          year,
        },
      });
      const { data, meta } = res?.data;
      setDataModal(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setModalTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingModal(false);
    }
  };
  const debounceDataModal = useCallback(
    debounce(() => {
      getDataModal();
    }, 500),
    [modalParams]
  );
  const getDetailSettlement = async id => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/advance-composer/v2/settlement-history/${id}`,
        {
          headers,
          params: modalParams,
        }
      );
      const { data, meta } = res?.data;
      setDataModal(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setModalTotalPage(pageCount || 1);
      setModalVisible(true);
      setModalTitle("Settlement Details");
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getDataSummary(), getDataTable()];
    if (isFirstLoad.current) {
      promises.push(getClientList());
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      fetchData();
    }
  }, [queryParams, debounceDataTable]);
  useEffect(() => {
    if (!isFirstLoad.current) {
      const isSearching = Boolean(filterParams?.search);
      if (isSearching) {
        debounceFilterClient();
        return () => {
          debounceFilterClient.cancel();
        };
      } else {
        getClientList();
      }
    }
  }, [filterParams, debounceFilterClient]);
  useEffect(() => {
    if (modalVisible) {
      const isSearching = Boolean(modalParams?.search);
      if (isSearching) {
        debounceDataModal();
        return () => {
          debounceDataModal.cancel();
        };
      } else {
        getDataModal();
      }
    }
  }, [modalParams, debounceDataModal]);

  return (
    <Page className={classes.root} title={"Composer/Author Advance"}>
      <Container maxWidth={false}>
        <HeaderTitle
          title="Composer/Author Advance"
          breadcrumbData={breadcrumbData}
        />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <Grid container columnGap={2} my="16px">
          <Grid item>
            <AutoCompleteWithSearch
              options={filterComposer}
              placeholder="All Composer"
              uniqueKey="id"
              value={
                optionClient.find(
                  option => option.id === queryParams.composer_id
                ) || null
              }
              onInputChange={value => handleChangeFilterParams(value, "search")}
              isOptionEqualToValue={(option, value) =>
                option?.composer_id === value?.composer_id ||
                option?.id === value?.id
              }
              inputValue={filterParams?.search}
              onChange={value => {
                handleChangeQueryParams(value?.composer_id, "composer_id");
              }}
              optionLabel={"composer_name"}
              hasMore={hasMore}
              loading={loadingFilter}
              width={300}
            />
          </Grid>
          <Grid item>
            <YearPicker
              label="Year"
              onChange={year => handleChangeQueryParams(year, "year")}
              value={queryParams?.year}
              width={200}
            />
          </Grid>
        </Grid>
        <Box display="flex" overflow="auto" columnGap={2} mt="16px">
          {dataSummary?.map((item, index) => {
            const cardType = item?.type;
            const isOpen = cardType === "open";
            const isTotal = item?.type === "total";
            return (
              <Box flexShrink={0} key={index}>
                <BalanceCard
                  title={item?.card_name}
                  value={item?.total_amount}
                  infoButton
                  tooltipTitle={`Composer/Author Advance Status ${
                    summaryAtrributes?.find(
                      attribute => attribute?.key === item?.type
                    )?.tooltip_title
                  }`}
                  onClick={() => {
                    setModalSettlement(false);
                    setModalVisible(true);
                    setModalTitle(item?.card_name);
                    handleChangeModalParams(
                      isTotal ? "" : item?.type,
                      "status"
                    );
                  }}
                  subTitle={`${item?.total_document} Docs`}
                  loading={loadingPage}
                  minWidth={350}
                  backgroundColor={isOpen && "#712D20"}
                />
              </Box>
            );
          })}
        </Box>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          my={2}
        >
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Grid item>
                <SearchTextInput
                  placeholder="Search"
                  value={queryParams?.search}
                  onChange={e =>
                    handleChangeQueryParams(e?.target?.value, "search")
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <SelectInput
              label="Status"
              placeholder="None"
              value={queryParams?.status}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "status")
              }
              options={optionsStatusAdvanceComposer}
              optionKey="value"
              optionLabel="name"
              width={200}
            />
          </Grid>
        </Grid>
        <Divider className={classes?.divider} />
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable(handleViewSettlement)}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleView={item =>
            history.push(`/admin/advance-composer/detail/${item?.id}`)
          }
        />
        {modalVisible && (
          <ModalTable
            open={modalVisible}
            onClose={() => setModalVisible(false)}
            title={modalTitle}
            isLoading={loadingModal}
            dataTable={dataModal}
            columnTable={
              modalSettlement ? columnTableSettlement : columnTable()
            }
            queryParams={modalParams}
            handleChangeQueryParams={handleChangeModalParams}
            tableTotalPage={modalTotalPage}
            search={!modalSettlement}
          />
        )}
      </Container>
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/advance-composer",
  },
  {
    label: "Composer/Author Advance",
    active: true,
  },
];
const summaryAtrributes = [
  { key: "total", tooltip_title: "Total" },
  { key: "closed", tooltip_title: "Closed" },
  { key: "open", tooltip_title: "Unclosed" },
];
const columnTable = handleView => [
  {
    name: "composer",
    title: "Composer/Author",
    renderText: item => item || "-",
  },
  {
    name: "date",
    title: "Date",
    renderText: date => formatDate(date),
  },
  {
    name: "document_number",
    title: "Contract Number",
    renderText: item => item || "-",
  },
  {
    name: "period",
    title: "Advance Period",
    renderText: item => formatPeriodFromString(item) || "-",
  },
  {
    name: "amount",
    title: "Total Advance",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} decimalScale={2} suffix=",-" prefix="Rp" />
    ),
  },
  {
    name: "all",
    title: "Settled Amount",
    headerAlign: "right",
    renderText: item =>
      handleView ? (
        <Box display="flex" justifyContent="right">
          <PrimaryTextButton
            tooltipTitle="See Settlement Details"
            onClick={() => handleView(item)}
            label={
              <CurrencyDisplay
                value={item?.paid_amount}
                decimalScale={2}
                suffix=",-"
                prefix="Rp"
              />
            }
          />
        </Box>
      ) : (
        <CurrencyDisplay
          value={item?.paid_amount}
          decimalScale={2}
          suffix=",-"
          prefix="Rp"
        />
      ),
  },
  {
    name: "remaining_amount",
    title: "Unsettled Amount",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} decimalScale={2} suffix=",-" prefix="Rp" />
    ),
  },
  {
    name: "status",
    title: "Status",
    renderText: status => (
      <StatusChip
        label={
          optionsStatusAdvanceComposer?.find(value => value?.value === status)
            ?.name || "-"
        }
        type={
          optionsStatusAdvanceComposer?.find(value => value?.value === status)
            ?.color || ""
        }
      />
    ),
  },
];
const columnTableSettlement = [
  {
    name: "sales_invoice_number",
    title: "Sales Invoice Number",
    renderText: item => item || "-",
  },
  {
    name: "date",
    title: "Date",
    renderText: date => formatDate(date),
  },
  {
    name: "amount",
    title: "Settled Amount",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} decimalScale={2} suffix=",-" prefix="Rp" />
    ),
  },
];
export default AdvanceComposer;
